import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { VideoFileDrop, type UploadResult } from "@/components/VideoFileDrop"
import { CoverImageDrop, type CoverImageResult } from "@/components/CoverImageDrop"
import { useAllCoaches } from "@/lib/queries/coaches"
import {
  useSendMessage,
  type MessageKind,
  type MessageTarget,
} from "@/lib/queries/messages"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Message compose (V-I)
// API:    POST /admin/message_send  { coach_id, kind, target, user_ids?,
//                                     topic?, body?, preview?, mediaUrl?,
//                                     mediaKey?, durationSec? }
//
// One submit creates one BATCH (one send event fanned out to every
// recipient). The audio file for `voice` messages must be uploaded
// BEFORE submit so the server can persist a stable mediaUrl/mediaKey.
//
// We reuse the existing /upload_video endpoint (via VideoFileDrop with
// audio-only accept) — the upload service stores audio files happily
// and returns the same { Location, Key } S3 envelope the rest of the
// app uses. Avoids inventing a parallel /admin/file_upload endpoint.
// ───────────────────────────────────────────────────────────────────────

const HEX24 = /^[0-9a-fA-F]{24}$/

const schema = z
  .object({
    coach_id: z.string().min(1, "Pick a coach"),
    kind: z.enum(["voice", "quote", "text", "short"]),
    topic: z.string().max(280).optional().default(""),
    body: z.string().max(4000).optional().default(""),
    preview: z.string().max(280).optional().default(""),
    target: z.enum(["followers", "specific"]),
    user_ids_raw: z.string().optional().default(""),
    // V-CoachSub Phase 3: general vs premium tag. Default general so
    // existing admin muscle-memory keeps producing universally-accessible
    // posts. Only fires gating for viewers without a per-coach sub.
    content_tier: z.enum(["general", "premium"]).default("general"),
  })
  .superRefine((v, ctx) => {
    if ((v.kind === "quote" || v.kind === "text") && !v.body.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["body"],
        message: "Body is required for quote and text messages.",
      })
    }
    // V-N shorts: topic is the headline shown above the body on the
    // mobile card, body is the caption/description. Both required.
    if (v.kind === "short" && !v.topic.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["topic"],
        message: "Topic is required for shorts (headline above the video).",
      })
    }
    if (v.kind === "short" && !v.body.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["body"],
        message: "Body is required for shorts (caption / description).",
      })
    }
    if (v.target === "specific") {
      const ids = v.user_ids_raw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      if (ids.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["user_ids_raw"],
          message: "Add at least one user ID.",
        })
      }
    }
  })

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  coach_id: "",
  kind: "voice",
  topic: "",
  body: "",
  preview: "",
  target: "followers",
  user_ids_raw: "",
  content_tier: "general",
}

export default function MessageCompose() {
  const navigate = useNavigate()
  const coachesQ = useAllCoaches()
  const sendMut = useSendMessage()

  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  // V-N shorts: separate cover (poster frame) upload alongside the mp4.
  // Tracked independently so each can show its own progress + error state.
  const [coverResult, setCoverResult] = useState<CoverImageResult | null>(null)
  const [isCoverUploading, setIsCoverUploading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successInfo, setSuccessInfo] = useState<{
    recipientCount: number
  } | null>(null)

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  const kind = watch("kind") as MessageKind
  const target = watch("target") as Exclude<MessageTarget, "both">
  const userIdsRaw = watch("user_ids_raw") || ""

  // Preview-time validation for "specific" IDs: spot malformed IDs early
  // so the admin doesn't fire a send only to find half their IDs were
  // typos. Backend will still re-validate.
  const malformedIds = useMemo(() => {
    if (target !== "specific") return []
    return userIdsRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((id) => !HEX24.test(id))
  }, [target, userIdsRaw])

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    setSuccessInfo(null)

    if (values.kind === "voice" && !uploadResult?.s3response) {
      setSubmitError("Upload an audio file before sending a voice message.")
      return
    }
    if (values.kind === "short") {
      if (!uploadResult?.s3response) {
        setSubmitError("Upload an mp4 before sending a short.")
        return
      }
      if (!coverResult?.s3response) {
        setSubmitError("Upload a cover image before sending a short.")
        return
      }
    }

    const s3 = uploadResult?.s3response as
      | { Location?: string; Key?: string }
      | undefined
    const coverS3 = coverResult?.s3response as
      | { Location?: string; Key?: string }
      | undefined

    const userIds =
      values.target === "specific"
        ? values.user_ids_raw
            .split(/[\s,]+/)
            .map((s) => s.trim())
            .filter(Boolean)
        : []

    try {
      const isShort = values.kind === "short"
      const res = await sendMut.mutateAsync({
        coach_id: values.coach_id,
        kind: values.kind,
        target: values.target,
        user_ids: userIds.length > 0 ? userIds : undefined,
        // topic = the headline. voice/short have always used it; now
        // quote/text send it too so the mobile Messages tab renders the
        // headline (e.g. "#Dreams") instead of a generic "Quote".
        topic:
          values.kind === "voice" ||
          isShort ||
          values.kind === "quote" ||
          values.kind === "text"
            ? values.topic || ""
            : undefined,
        body:
          values.kind === "quote" ||
          values.kind === "text" ||
          isShort ||
          values.body
            ? values.body || ""
            : undefined,
        preview:
          values.kind === "text" ? values.preview || "" : undefined,
        mediaUrl:
          values.kind === "voice" || isShort ? s3?.Location || "" : undefined,
        mediaKey:
          values.kind === "voice" || isShort ? s3?.Key || "" : undefined,
        coverUrl: isShort ? coverS3?.Location || "" : undefined,
        coverKey: isShort ? coverS3?.Key || "" : undefined,
        content_tier: values.content_tier,
      })
      if (!res?.success) {
        setSubmitError(res?.message || "Failed to send message.")
        return
      }
      const count = res.data?.recipientCount ?? 0
      setSuccessInfo({ recipientCount: count })
      // Brief on-page confirmation, then bounce to the list. Mirrors how
      // CoachForm hands off after a successful create.
      window.setTimeout(() => navigate("/messages"), 700)
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to send message."
      )
    }
  })

  const submitting = sendMut.isPending
  const disabled = submitting || isUploading || isCoverUploading

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/messages">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to messages
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Send message</CardTitle>
          <CardDescription>
            Compose one message and fan it out to followers (or a specific
            list of users).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit} noValidate>
            {/* Coach picker */}
            <div className="space-y-2">
              <Label htmlFor="coach_id">
                Creator<span className="text-destructive"> *</span>
              </Label>
              <Controller
                control={control}
                name="coach_id"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={coachesQ.isLoading || disabled}
                  >
                    <SelectTrigger
                      id="coach_id"
                      aria-invalid={!!errors.coach_id}
                    >
                      <SelectValue
                        placeholder={
                          coachesQ.isLoading
                            ? "Loading creators…"
                            : "Choose a creator"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {coachesQ.data?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            {c.avatar_url ? (
                              <img
                                src={c.avatar_url}
                                alt=""
                                className="h-5 w-5 rounded-full object-cover"
                              />
                            ) : (
                              <span className="grid h-5 w-5 place-items-center rounded-full bg-muted text-[10px] text-muted-foreground">
                                —
                              </span>
                            )}
                            {c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.coach_id && (
                <p className="text-xs text-destructive">
                  {errors.coach_id.message}
                </p>
              )}
            </div>

            {/* Kind */}
            <div className="space-y-2">
              <Label>
                Kind<span className="text-destructive"> *</span>
              </Label>
              <Controller
                control={control}
                name="kind"
                render={({ field }) => (
                  <div
                    role="radiogroup"
                    aria-label="Message kind"
                    className="flex flex-wrap gap-2"
                  >
                    {(["voice", "quote", "text", "short"] as const).map((k) => (
                      <label
                        key={k}
                        className={
                          "cursor-pointer rounded-md border px-3 py-2 text-sm capitalize transition-colors " +
                          (field.value === k
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-input text-muted-foreground hover:text-foreground")
                        }
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          name={field.name}
                          value={k}
                          checked={field.value === k}
                          onChange={() => field.onChange(k)}
                          disabled={disabled}
                        />
                        {k}
                      </label>
                    ))}
                  </div>
                )}
              />
              <p className="text-xs text-muted-foreground">
                {kind === "voice" && "Recorded audio with optional caption."}
                {kind === "quote" && "Short quote the user can save."}
                {kind === "text" &&
                  "Longer text with a short preview shown collapsed."}
                {kind === "short" &&
                  "Vertical mp4 (≤ 60s, ≤ 100 MB) with a poster image. Headline + caption required."}
              </p>
            </div>

            {/* Topic / Headline — every kind. For quote/text it's the
                headline the mobile Messages tab + Library show above the
                body (e.g. "#Dreams"); without it those cards fall back to
                a generic "Quote". */}
            <div className="space-y-2">
              <Label htmlFor="topic">
                {kind === "short" || kind === "quote" || kind === "text"
                  ? "Headline"
                  : "Topic"}
                {kind === "short" && (
                  <span className="text-destructive"> *</span>
                )}
              </Label>
              <Input
                id="topic"
                placeholder={
                  kind === "short"
                    ? "What's this short about? (headline above the video)"
                    : kind === "quote"
                      ? "Headline shown above the quote (e.g. #Dreams)"
                      : kind === "text"
                        ? "Headline shown above the note (e.g. #Mindset)"
                        : "What's this voice note about?"
                }
                aria-invalid={!!errors.topic}
                {...register("topic")}
              />
              {errors.topic && (
                <p className="text-xs text-destructive">
                  {errors.topic.message}
                </p>
              )}
            </div>

            {/* Body — required for quote + text, optional caption for voice */}
            <div className="space-y-2">
              <Label htmlFor="body">
                {kind === "voice"
                  ? "Caption"
                  : kind === "short"
                    ? "Caption"
                    : "Body"}
                {kind !== "voice" && (
                  <span className="text-destructive"> *</span>
                )}
              </Label>
              <Textarea
                id="body"
                rows={kind === "text" ? 6 : 4}
                placeholder={
                  kind === "voice"
                    ? "Optional caption shown alongside the audio."
                    : kind === "quote"
                      ? "The quote text."
                      : kind === "short"
                        ? "Caption / description shown beneath the headline."
                        : "The full message body."
                }
                aria-invalid={!!errors.body}
                {...register("body")}
              />
              {errors.body && (
                <p className="text-xs text-destructive">
                  {errors.body.message}
                </p>
              )}
            </div>

            {/* Preview — text only */}
            {kind === "text" && (
              <div className="space-y-2">
                <Label htmlFor="preview">Preview</Label>
                <Input
                  id="preview"
                  placeholder="Short summary shown before expansion."
                  aria-invalid={!!errors.preview}
                  {...register("preview")}
                />
                <p className="text-xs text-muted-foreground">
                  Shown collapsed in the inbox; the full body opens on tap.
                </p>
              </div>
            )}

            {/* Audio file — voice only */}
            {kind === "voice" && (
              <div className="space-y-2">
                <Label>
                  Audio file<span className="text-destructive"> *</span>
                </Label>
                <VideoFileDrop
                  accept="audio/*"
                  result={uploadResult}
                  onResult={setUploadResult}
                  onUploadingChange={setIsUploading}
                  disabled={submitting}
                />
                {uploadResult && !isUploading && (
                  <p className="text-xs text-green-700">
                    ✓ Uploaded ({uploadResult.file_name})
                  </p>
                )}
              </div>
            )}

            {/* Video + cover — short only.
                Two separate uploads through the same /upload_video pipeline:
                  - Video tile accepts mp4/mov/m4v
                  - Cover tile accepts image/* and renders an inline preview.
                Server validates required-mediaUrl + required-coverUrl on
                /admin/message_send when kind="short". */}
            {kind === "short" && (
              <>
                <div className="space-y-2">
                  <Label>
                    Video file (mp4)
                    <span className="text-destructive"> *</span>
                  </Label>
                  <VideoFileDrop
                    accept="video/mp4,video/quicktime,video/x-m4v"
                    result={uploadResult}
                    onResult={setUploadResult}
                    onUploadingChange={setIsUploading}
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    Vertical 9:16 preferred. ≤ 60 seconds, ≤ 100 MB. The
                    server probes durationSec via ffprobe at upload time —
                    no manual entry needed.
                  </p>
                  {uploadResult && !isUploading && (
                    <p className="text-xs text-green-700">
                      ✓ Uploaded ({uploadResult.file_name})
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>
                    Cover image (poster)
                    <span className="text-destructive"> *</span>
                  </Label>
                  <CoverImageDrop
                    result={coverResult}
                    onResult={setCoverResult}
                    onUploadingChange={(b) => setIsCoverUploading(b)}
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    JPG / PNG / WEBP / HEIC, ≤ 5 MB. Shown as the card's
                    thumbnail and the player's poster while the video
                    loads.
                  </p>
                </div>
              </>
            )}

            {/* Target */}
            <div className="space-y-2">
              <Label>
                Target<span className="text-destructive"> *</span>
              </Label>
              <Controller
                control={control}
                name="target"
                render={({ field }) => (
                  <div
                    role="radiogroup"
                    aria-label="Target audience"
                    className="space-y-2"
                  >
                    {(
                      [
                        {
                          value: "followers",
                          label: "All followers of this creator",
                        },
                        { value: "specific", label: "Specific user IDs" },
                      ] as const
                    ).map((opt) => (
                      <label
                        key={opt.value}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value={opt.value}
                          checked={field.value === opt.value}
                          onChange={() => field.onChange(opt.value)}
                          disabled={disabled}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}
              />
            </div>

            {/* V-CoachSub Phase 3: general vs premium tier.
                Premium messages are gated to viewers with an active
                per-coach subscription to the selected coach. General
                is accessible to everyone. */}
            <div className="space-y-2">
              <Label>
                Content tier<span className="text-destructive"> *</span>
              </Label>
              <Controller
                control={control}
                name="content_tier"
                render={({ field }) => (
                  <div
                    role="radiogroup"
                    aria-label="Content tier"
                    className="space-y-2"
                  >
                    {(
                      [
                        {
                          value: "general",
                          label:
                            "General — anyone can view this message",
                        },
                        {
                          value: "premium",
                          label:
                            "Premium — only viewers with an active per-coach subscription to this coach",
                        },
                      ] as const
                    ).map((opt) => (
                      <label
                        key={opt.value}
                        className="flex cursor-pointer items-start gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value={opt.value}
                          checked={field.value === opt.value}
                          onChange={() => field.onChange(opt.value)}
                          disabled={disabled}
                          className="mt-0.5"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              />
            </div>

            {target === "specific" && (
              <div className="space-y-2">
                <Label htmlFor="user_ids_raw">User IDs</Label>
                <Textarea
                  id="user_ids_raw"
                  rows={3}
                  placeholder="Comma- or whitespace-separated 24-char ObjectIds"
                  aria-invalid={!!errors.user_ids_raw}
                  {...register("user_ids_raw")}
                />
                {errors.user_ids_raw && (
                  <p className="text-xs text-destructive">
                    {errors.user_ids_raw.message}
                  </p>
                )}
                {malformedIds.length > 0 && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {malformedIds.length} ID
                      {malformedIds.length === 1 ? "" : "s"} look
                      malformed (must be 24-char hex):{" "}
                      <code>{malformedIds.slice(0, 3).join(", ")}</code>
                      {malformedIds.length > 3 ? "…" : ""}
                    </span>
                  </div>
                )}
              </div>
            )}

            {submitError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            {successInfo && (
              <div
                role="status"
                className="flex items-start gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-200"
              >
                <span>
                  Sent to {successInfo.recipientCount.toLocaleString()}{" "}
                  recipient
                  {successInfo.recipientCount === 1 ? "" : "s"}.
                  Redirecting…
                </span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={disabled}>
                {(submitting || isUploading || isCoverUploading) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isUploading
                  ? "Uploading video…"
                  : isCoverUploading
                    ? "Uploading cover…"
                    : submitting
                      ? "Sending…"
                      : "Send message"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/messages")}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
